import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, Calendar, Clock } from "lucide-react";
import api from "../lib/api";

/**
 * Auto-suggested related posts strip. Renders at the bottom of any blog
 * post; scored server-side by shared-tag overlap, ties broken by recency.
 *
 * Hidden when no related posts exist (single-post blog) so we never show
 * an empty section.
 *
 * SEO win: every post now links to 2-3 others → boosts Google's crawl
 * depth + lowers bounce rate by keeping readers in your blog longer.
 */
export default function RelatedPosts({ slug }) {
  const [posts, setPosts] = useState(null);

  useEffect(() => {
    if (!slug) return;
    let cancelled = false;
    api
      .get(`/blog/posts/${slug}/related?limit=3`)
      .then((r) => !cancelled && setPosts(r.data?.posts || []))
      .catch(() => !cancelled && setPosts([]));
    return () => {
      cancelled = true;
    };
  }, [slug]);

  if (!posts || posts.length === 0) return null;

  return (
    <section
      className="mt-10 bg-slate-50 border border-slate-200 rounded-sm p-6"
      data-testid="related-posts"
    >
      <h3 className="text-xs font-bold uppercase tracking-widest text-slate-500 mb-4">
        Continue reading
      </h3>
      <ul className="grid grid-cols-1 md:grid-cols-3 gap-4">
        {posts.map((p) => (
          <li key={p.slug}>
            <Link
              to={`/blog/${p.slug}`}
              className="block bg-white border border-slate-200 hover:border-slate-900 rounded-sm p-4 h-full transition-colors group"
              data-testid={`related-post-${p.slug}`}
            >
              <h4
                className="text-sm font-bold text-slate-900 group-hover:text-emerald-700 transition-colors leading-snug"
                style={{ fontFamily: "Outfit" }}
              >
                {p.title}
              </h4>
              <p className="text-xs text-slate-600 mt-1.5 line-clamp-3">
                {p.description}
              </p>
              <div className="flex items-center gap-3 text-[10px] text-slate-500 mt-2.5">
                <span className="inline-flex items-center gap-1">
                  <Calendar className="w-2.5 h-2.5" /> {formatDate(p.date)}
                </span>
                <span className="inline-flex items-center gap-1">
                  <Clock className="w-2.5 h-2.5" /> {p.reading_time_min} min
                </span>
              </div>
              <div className="mt-2 inline-flex items-center gap-1 text-[10px] font-semibold text-emerald-700">
                Read article <ArrowRight className="w-2.5 h-2.5" />
              </div>
            </Link>
          </li>
        ))}
      </ul>
    </section>
  );
}

function formatDate(iso) {
  try {
    return new Date(iso + "T00:00:00Z").toLocaleDateString("en-US", {
      year: "numeric",
      month: "short",
      day: "numeric",
    });
  } catch {
    return iso;
  }
}
